import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { purchaseFlow } from '../../help/flows';
import { GOODS_RECEIPT_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { GoodsReceipt, GoodsReceiptStatus } from '../../interfaces/procurement/goodsReceipt.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import SerialPostDialog from '../serial/SerialPostDialog'; // F6b
import { LineSerialsRequest } from '../../interfaces/inventory/serial.interface'; // F6b
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K
import { ExchangeRateService } from '../../services/exchangeRate.service'; // G5a
import { formatFc, isForeignCurrency, toastSaved } from '../../utils/fcDocuments'; // G5a

const STATUS_COLORS: Record<GoodsReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  POSTED: 'success',
  REVERSED: 'error',
};

export const GoodsReceiptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canPost = usePermission(PERMISSIONS.GOODS_RECEIPT_POST); // post + reverse are GOODS_RECEIPT_POST server-side
  const canManageGrn = usePermission(PERMISSIONS.GOODS_RECEIPT_MANAGE); // W3-K attachments
  const [grn, setGrn] = useState<GoodsReceipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  // G5a: the booking rate of a foreign draft receipt, prefilled from the table on the receipt date (never the PO rate)
  const [bookingRate, setBookingRate] = useState<number | ''>('');
  const [rateHint, setRateHint] = useState('');
  const foreign = !!grn && isForeignCurrency(grn.currencyCode);
  const foreignDraft = foreign && grn?.status === 'DRAFT';

  useEffect(() => {
    if (!id) return;
    GoodsReceiptService.getById(id)
      .then(setGrn)
      .catch(e => toast.showApiError(e, 'Failed to load goods receipt'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!grn || !foreignDraft || !grn.currencyCode) return;
    let live = true;
    ExchangeRateService.latest(grn.institutionId, grn.currencyCode, grn.receiptDate)
      .then(r => {
        if (!live) return;
        setBookingRate(prev => (prev === '' ? r.rate : prev));
        setRateHint(`Rate of ${r.rateDate} from the exchange-rate table; the PO rate was ${grn.exchangeRate ?? '—'}`);
      })
      .catch(() => { if (live) setRateHint(`No ${grn.currencyCode} rate on file for ${grn.receiptDate}: enter the rate`); });
    return () => { live = false; };
  }, [grn?.id, foreignDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePost = async (serials?: LineSerialsRequest) => {
    if (!grn) return;
    setPosting(true);
    try {
      const updated = foreignDraft && Number(bookingRate) > 0
        ? await GoodsReceiptService.post(grn.id, serials, Number(bookingRate))
        : await GoodsReceiptService.post(grn.id, serials);
      setGrn(updated);
      toastSaved(toast, 'Goods receipt posted', updated.warnings);
      setPostDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to post GRN');
    } finally {
      setPosting(false);
    }
  };

  const handleReverse = async () => {
    if (!grn) return;
    setReversing(true);
    try {
      const updated = await GoodsReceiptService.reverse(grn.id);
      setGrn(updated);
      toast.showSuccess('GRN reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse GRN');
    } finally {
      setReversing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!grn) return <Box sx={{ p: 3 }}><Typography>Goods receipt not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="store.grn.page">
      <FlowStrip data-help="store.grn.flow" stages={purchaseFlow({ current: 'grn', po: { id: grn.purchaseOrderId, no: grn.purchaseOrderNumber }, grn: { no: grn.grnNumber } })} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{grn.grnNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canPost && grn.status === 'DRAFT' && (
            <Button data-help="store.grn.post" variant="contained" color="primary" onClick={() => setPostDialogOpen(true)} disabled={posting}>
              {posting ? <CircularProgress size={20} /> : 'Post'}
            </Button>
          )}
          {canPost && grn.status === 'POSTED' && (
            <Button data-help="store.grn.reverse" variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} data-help-section="store.grn.header">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">GRN Number</Typography>
            <Typography>{grn.grnNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <StatusStagesChip data-help="store.grn.statusStages" label={humanizeEnum(grn.status)} status={grn.status} stages={GOODS_RECEIPT_STAGES} color={STATUS_COLORS[grn.status]} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Purchase Order</Typography>
            <Typography>{grn.purchaseOrderNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Store</Typography>
            <Typography>{grn.storeName ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Date</Typography>
            <Typography>{formatDate(grn.receiptDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Value</Typography>
            <Typography>{formatMoney(grn.totalValue)}</Typography>
            {foreign && grn.currencyCode && (
              <Typography variant="caption" color="text.secondary" display="block">
                {formatFc(grn.currencyCode, grn.foreignTotalValue)} at {grn.exchangeRate}{grn.status === 'DRAFT' ? ' (indicative PO rate)' : ' (booking rate)'}
              </Typography>
            )}
          </Box>
          {foreignDraft && grn.currencyCode && (
            <Box sx={{ gridColumn: 'span 2', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField data-help="procurement.grnDetail.field.bookingRate" label={`Booking rate (BDT per ${grn.currencyCode})`} type="number" value={bookingRate}
                onChange={e => { setRateHint(''); setBookingRate(e.target.value === '' ? '' : Number(e.target.value)); }}
                inputProps={{ min: 0, step: 'any' }} disabled={!canPost}
                helperText={rateHint || 'Stock is valued at this rate when the receipt is posted'}
                sx={{ flex: '1 1 220px', maxWidth: 320 }} />
            </Box>
          )}
          {grn.notes && (
            <Box sx={{ gridColumn: 'span 2' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{grn.notes}</Typography>
            </Box>
          )}
          {grn.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{grn.reversedDate ? formatDateTime(grn.reversedDate) : '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }} data-help-section="store.grn.lines">
        <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
        <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="store.grn.col.item">Item</TableCell>
                  <TableCell data-help="store.grn.col.qty">Received Qty</TableCell>
                  {foreign && <TableCell data-help="store.grn.col.fcPrice">Unit Price ({grn.currencyCode})</TableCell>}
                  <TableCell data-help="store.grn.col.price">Unit Price{foreign ? ' (BDT)' : ''}</TableCell>
                  <TableCell data-help="store.grn.col.value">Line Value{foreign ? ' (BDT)' : ''}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(grn.lines ?? []).map(line => (
                  <TableRow key={line.id}>
                    <TableCell>{line.itemName ?? '—'}{line.itemCode ? ` (${line.itemCode})` : ''}
                      {line.expiryDate && <Typography variant="caption" display="block" color="text.secondary">Expires {line.expiryDate}{line.manufactureDate ? `, made ${line.manufactureDate}` : ''}</Typography>}{/* F6a */}
                    </TableCell>
                    <TableCell>{line.receivedQty}{line.unit ? ` ${line.unit}` : ''}</TableCell>
                    {foreign && <TableCell>{line.foreignUnitPrice ?? '—'}</TableCell>}
                    <TableCell>{formatMoney(line.unitPrice)}</TableCell>
                    <TableCell>{formatMoney(line.lineValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </ScrollableTable>
      </Paper>

      {/* F6b: serial-tracked lines capture one serial per unit on post */}
      <SerialPostDialog
        open={postDialogOpen}
        title="Post this goods receipt?"
        message="Posting will add the received quantities to stock and create a journal entry. This cannot be undone."
        confirmLabel="Post"
        lines={(grn.lines ?? []).map(l => ({
          lineId: l.id, itemId: l.itemId, label: l.itemName ?? l.itemCode ?? 'Item', quantity: Number(l.receivedQty), mode: 'capture' as const,
        }))}
        onCancel={() => setPostDialogOpen(false)}
        onConfirm={handlePost}
        submitting={posting}
      />

      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Goods Receipt"
        consequence="Stock will be reversed and a reversing journal entry posted."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />
      {id && <AttachmentsPanel entityType="GOODS_RECEIPT" entityId={id} canManage={(canManageGrn || canPost)} />} {/* W3-K */}
    </Box>
  );
};

export default GoodsReceiptDetail;
